/**
 * Test Configuration Service
 *
 * Frontend service for managing Test Configurations and Test Runs.
 * Uses Firebase client SDK directly following flowService.ts pattern.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { apiRequest } from './api';
import { createOrgScopedQuery, validateQueryResults } from '../lib/firestore-helpers';
import { uniqueConfigName } from '../utils/uniqueConfigName';
import type {
  TestConfiguration,
  TestRun,
  BatchExecution,
  LocationRequirement,
  ExecutionTiming,
  ESimSelectionConfig,
  ExecutionStatus,
  FlowType,
} from '@simcheck/shared-types';
import { executionContextFromConfig } from '@simcheck/shared-types';

// ============================================================================
// TYPE CONVERSIONS
// ============================================================================

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return undefined;
}

function docToTestConfiguration(id: string, data: any): TestConfiguration {
  const c: any = {
    id,
    organizationId: data.organizationId,
    name: data.name,
    description: data.description || undefined,
    // Flow reference
    flowId: data.flowId || undefined,
    flowName: data.flowName || undefined,
    flowType: data.flowType || undefined,
    flowVersion: data.flowVersion || undefined,
    // Execution settings
    locationRequirements: data.locationRequirements || [],
    timing: {
      type: data.timing?.type || 'now',
      executeAt: toDate(data.timing?.executeAt),
      recurrence: data.timing?.recurrence
        ? {
            interval: data.timing.recurrence.interval,
            unit: data.timing.recurrence.unit,
            startDate: toDate(data.timing.recurrence.startDate),
            endDate: toDate(data.timing.recurrence.endDate),
            cronExpression: data.timing.recurrence.cronExpression,
          }
        : undefined,
    },
    esim: data.esim || { mode: 'none' },
    priority: data.priority || 5,
    useEmulator: data.useEmulator || false,
    createdBy: data.createdBy,
    createdAt: toDate(data.createdAt) || new Date(),
    updatedAt: toDate(data.updatedAt) || new Date(),
    lastUsedAt: toDate(data.lastUsedAt),
    usageCount: data.usageCount || 0,
    // Scheduler bookkeeping (recurring configs; written server-side by
    // TestConfigurationService + advanced by processTestSchedules). The
    // Upcoming tab projects nextFireAt as the schedule's next occurrence.
    isRecurring: data.isRecurring || undefined,
    nextFireAt: toDate(data.nextFireAt),
    lastFiredAt: toDate(data.lastFiredAt),
    paused: data.paused ?? undefined,
    scheduleState: data.scheduleState || undefined,
  };
  // Optional execution-context extras (not on the canonical TestConfiguration
  // type yet). Edit-mode prefill + dispatchRunFromConfig both read via
  // (c as any).field, so attach loosely.
  if (data.forcedRat !== undefined) c.forcedRat = data.forcedRat;
  if (data.forcedNetworkMccMnc !== undefined) c.forcedNetworkMccMnc = data.forcedNetworkMccMnc;
  if (data.timeZone !== undefined) c.timeZone = data.timeZone;
  // Saved flow-input values (number to dial, APN, sequences…). This mapper is
  // an EXPLICIT allowlist, so omitting `inputs` here silently dropped them on
  // the way out of Firestore: every consumer saw `config.inputs === undefined`,
  // so the editor opened blank and a Run fell back to the flow's defaults —
  // i.e. a per-configuration parameter could never take effect.
  if (data.inputs !== undefined) c.inputs = data.inputs;
  return c as TestConfiguration;
}

function docToTestRun(id: string, data: any): TestRun {
  return {
    id,
    organizationId: data.organizationId,
    flowId: data.flowId,
    flowName: data.flowName,
    flowType: data.flowType,
    flowVersion: data.flowVersion,
    testConfigurationId: data.testConfigurationId || undefined,
    configuration: docToTestConfiguration('inline', data.configuration),
    executionId: data.executionId || undefined,
    status: data.status,
    triggerSource: data.triggerSource || (data.scheduledFor ? 'scheduled' : 'manual'),
    // Allocation snapshot written by QueueService.startExecution. Used by the
    // Running tab columns and the Test Run Details modal to show *where* a
    // test is actually running without joining executions/{id}.
    // (Not in the canonical TestRun TS type — additive Firestore field.)
    ...(data.allocation ? { allocation: data.allocation } : {}),
    // #283 — a connectivity campaign is a test_run of a different `kind`, and the page picks
    // its detail surface from it. This mapper is a WHITELIST, so omitting these made every
    // campaign read as an ordinary flow run in the console: the second time the exact same
    // omission bit (the server's `TestRunService.docToRun` was the first). If you add a field
    // to a run, add it in BOTH mappers.
    ...(data.kind ? { kind: data.kind } : {}),
    ...(data.campaign ? { campaign: data.campaign } : {}),
    ...(data.parentCampaignRunId ? { parentCampaignRunId: data.parentCampaignRunId } : {}),
    createdAt: toDate(data.createdAt) || new Date(),
    scheduledFor: toDate(data.scheduledFor),
    startedAt: toDate(data.startedAt),
    completedAt: toDate(data.completedAt),
    createdBy: data.createdBy,
    result: data.result
      ? {
          success: data.result.success,
          duration: data.result.duration || undefined,
          error: data.result.error || undefined,
          summary: data.result.summary || undefined,
          outputs: data.result.outputs || undefined,
        }
      : undefined,
  };
}

function docToBatchExecution(id: string, data: any): BatchExecution {
  return {
    id,
    organizationId: data.organizationId,
    name: data.name,
    flowId: data.flowId,
    testConfigurationId: data.testConfigurationId || undefined,
    totalRuns: data.totalRuns,
    completedRuns: data.completedRuns,
    successfulRuns: data.successfulRuns,
    failedRuns: data.failedRuns,
    status: data.status,
    testRunIds: data.testRunIds || [],
    createdBy: data.createdBy,
    createdAt: toDate(data.createdAt) || new Date(),
    startedAt: toDate(data.startedAt),
    completedAt: toDate(data.completedAt),
  };
}

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function removeUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result as Partial<T>;
}

function serializeTiming(timing: ExecutionTiming): any {
  const serialized: any = { type: timing.type };

  if (timing.executeAt) {
    serialized.executeAt = Timestamp.fromDate(
      timing.executeAt instanceof Date ? timing.executeAt : new Date(timing.executeAt)
    );
  }

  if (timing.recurrence) {
    serialized.recurrence = {
      interval: timing.recurrence.interval,
      unit: timing.recurrence.unit,
    };
    if (timing.recurrence.startDate) {
      serialized.recurrence.startDate = Timestamp.fromDate(
        timing.recurrence.startDate instanceof Date
          ? timing.recurrence.startDate
          : new Date(timing.recurrence.startDate)
      );
    }
    if (timing.recurrence.endDate) {
      serialized.recurrence.endDate = Timestamp.fromDate(
        timing.recurrence.endDate instanceof Date
          ? timing.recurrence.endDate
          : new Date(timing.recurrence.endDate)
      );
    }
    if (timing.recurrence.cronExpression) {
      serialized.recurrence.cronExpression = timing.recurrence.cronExpression;
    }
  }

  return serialized;
}

// ============================================================================
// TEST CONFIGURATIONS
// ============================================================================

/**
 * Dispatch a run from a (saved or snapshot) TestConfiguration.
 *
 * Single entry point used by:
 *   - Row "Run test" play icon  (one-click from a saved config)
 *   - History "Retry" button   (re-run from the snapshot on a test_run, NOT the
 *                                live config — see plans/tests-page-audit.md BUG-E)
 *   - The Run Test Sheet itself when submitting an existing saved config
 *
 * Creates a test_run row, then fires runtimeService.executeFlow as
 * fire-and-forget with testRunId. The backend handles all subsequent
 * test_runs status syncs from Admin SDK (QueueService.addToQueue +
 * startExecution + completeExecution).
 */
export async function dispatchRunFromConfig(
  organizationId: string,
  userId: string,
  config: TestConfiguration,
  /** Saved config id (omit for snapshot retries). */
  savedConfigId?: string,
): Promise<{ runId: string }> {
  // Lazy imports to avoid circular deps + smaller initial bundles.
  const { getFlow } = await import('./flowService');
  const runtime = await import('./runtime');

  if (!config.flowId) {
    throw new Error('Configuration has no flowId — cannot run');
  }

  const flowDef = await getFlow(config.flowId, organizationId);
  if (!flowDef) {
    throw new Error('Flow not found (or inaccessible)');
  }

  const scheduledFor =
    config.timing.type === 'scheduled' && config.timing.executeAt
      ? (config.timing.executeAt instanceof Date ? config.timing.executeAt : new Date(config.timing.executeAt))
      : undefined;

  const run = await createTestRun(organizationId, userId, {
    flowId: config.flowId,
    flowName: config.flowName || flowDef.name,
    flowType: (config.flowType || 'scenario') as any,
    flowVersion: config.flowVersion || (flowDef as any).version || 1,
    testConfigurationId: savedConfigId,
    configuration: config,
    scheduledFor,
  });

  if (scheduledFor) {
    return { runId: run.id }; // backend cron handles schedule
  }

  const locReq = config.locationRequirements?.[0];
  const locationId = locReq?.locationId;
  const clusterId = locReq?.clusterId;
  // ONE config → execution-context mapping shared with ExecutionDialog and
  // the backend scheduler (#209: the group binding was present here and
  // dropped elsewhere). Carries eSIM / group / RAT / PLMN / modem pin (#252).
  const executionContext = executionContextFromConfig(config as any);

  // Await the submit so pre-flight failures (NO_CAPABLE_MODEM 409 from
  // structurally impossible constraints — e.g. an IoT-MQTT flow submitted
  // with forcedRat=nr5g, no modem at any location can satisfy both) reach
  // the caller. Without this the error was console.error'd and the run
  // sat 'queued' forever in the UI. Backend returns 202 in <500ms; the
  // backend still writes test_runs.executionId / status / result async
  // via the testRunId reference for the happy path.
  try {
    await runtime.executeFlow(
      flowDef,
      // The saved config's flow-input values (number to dial, APN, sequences…)
      // MUST ride along — without them a run launched from the Tests page fell
      // back to whatever defaults the flow declares, so a per-configuration
      // value was silently ignored and every config of a flow behaved
      // identically. Mirrors the one-off path in ExecutionDialog, which already
      // spreads `runtimeConfig.inputs` here.
      { clusterId, ...(((config as any).inputs as Record<string, any>) || {}) },
      userId,
      organizationId,
      executionContext,
      locationId,
      config.useEmulator,
      run.id,
    );
  } catch (err: any) {
    console.error('[dispatchRunFromConfig] executeFlow submit error:', err);
    // The backend's pre-flight already marked the execution + test_run
    // as failed with the structured reason before returning the 409, so
    // the Tests list will reflect the failure. Bubbling the error lets
    // the caller (Tests page handleQuickRunConfig) surface a toast too.
    throw err;
  }

  return { runId: run.id };
}

/**
 * Get all test configurations for an organization
 */
export async function getTestConfigurations(organizationId: string): Promise<TestConfiguration[]> {
  const q = createOrgScopedQuery('test_configurations', organizationId, [
    orderBy('updatedAt', 'desc'),
  ]);
  const snapshot = await getDocs(q);

  const configs = snapshot.docs.map((doc) =>
    docToTestConfiguration(doc.id, doc.data())
  );

  if (import.meta.env.DEV) {
    validateQueryResults(configs, organizationId, 'test_configurations');
  }

  return configs;
}

/**
 * Get a single test configuration by ID
 */
export async function getTestConfiguration(
  configId: string,
  organizationId: string
): Promise<TestConfiguration | null> {
  const docRef = doc(db, 'test_configurations', configId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const config = docToTestConfiguration(docSnap.id, docSnap.data());

  // System configs (isSystem) are accessible to all organizations
  const isSystemConfig = (docSnap.data() as any)?.isSystem === true;

  // Verify tenant isolation (skip for system configs)
  if (!isSystemConfig && config.organizationId !== organizationId) {
    throw new Error('Unauthorized access to test configuration');
  }

  return config;
}

/**
 * Create a new test configuration
 */
export async function createTestConfiguration(
  organizationId: string,
  userId: string,
  data: {
    name: string;
    description?: string;
    // Flow reference
    flowId?: string;
    flowName?: string;
    flowType?: FlowType;
    flowVersion?: number;
    // Execution settings
    locationRequirements: LocationRequirement[];
    timing: ExecutionTiming;
    esim: ESimSelectionConfig;
    priority?: number;
    useEmulator?: boolean;
    // Optional execution context fields persisted top-level on the doc and
    // re-read by defaultsFromConfig + dispatchRunFromConfig. Not in
    // @simcheck/shared-types yet (would require a coordinated update + dist
    // rebuild); kept loose-typed for now.
    forcedRat?: string;
    forcedNetworkMccMnc?: string;
    timeZone?: string;
    // Flow input parameter values (apn, dialNumber, etc.).
    inputs?: Record<string, any>;
  }
): Promise<TestConfiguration> {
  // Route through the backend (api function) rather than a client-side addDoc.
  // The backend's TestConfigurationService.create is the single source of truth
  // for scheduler bookkeeping: it computes nextFireAt / isRecurring / paused for
  // recurring configs and persists forcedRat / forcedNetworkMccMnc / timeZone /
  // inputs. A direct client write skips all of that, so recurring configs would
  // be invisible to the processTestSchedules cron. (updateTestConfiguration
  // already routes through the backend for an analogous reason.)
  const { apiRequest } = await import('./api');
  const created = await apiRequest<TestConfiguration>(
    '/test-configurations',
    {
      method: 'POST',
      body: JSON.stringify({
        organizationId,
        name: data.name,
        description: data.description,
        flowId: data.flowId,
        flowName: data.flowName,
        locationRequirements: data.locationRequirements,
        timing: data.timing,
        esim: data.esim,
        priority: data.priority,
        useEmulator: data.useEmulator,
        ...(data.forcedRat ? { forcedRat: data.forcedRat } : {}),
        ...(data.forcedNetworkMccMnc ? { forcedNetworkMccMnc: data.forcedNetworkMccMnc } : {}),
        ...(data.timeZone ? { timeZone: data.timeZone } : {}),
        ...(data.inputs ? { inputs: data.inputs } : {}),
      }),
    },
    organizationId,
  );
  return created;
}

/**
 * Update a test configuration
 */
export async function updateTestConfiguration(
  configId: string,
  organizationId: string,
  updates: Partial<{
    name: string;
    description: string;
    // Flow reference
    flowId: string;
    flowName: string;
    flowType: FlowType;
    flowVersion: number;
    // Execution settings
    locationRequirements: LocationRequirement[];
    timing: ExecutionTiming;
    esim: ESimSelectionConfig;
    priority: number;
    useEmulator: boolean;
    // Pause/resume a recurring schedule. Backend-only semantics: resume
    // re-anchors nextFireAt so a long-paused schedule never back-fires.
    paused: boolean;
  }>
): Promise<void> {
  // Route through the backend (api function) so the write runs from Admin SDK
  // context. The client-side updateDoc(test_configurations, ...) path used to
  // live here and failed with PERMISSION_DENIED for every non-system config
  // because of an orgIdUnchanged() rule evaluation fault — see
  // plans/tests-page-audit.md. The backend already has access checks
  // (route verifies org membership before forwarding to Admin SDK).
  const { apiRequest } = await import('./api');
  await apiRequest(`/test-configurations/${configId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }, organizationId);
}

/**
 * Delete a test configuration. Routed through the backend so the write runs
 * from Admin SDK and bypasses the broken Firestore rule (see above).
 */
export async function deleteTestConfiguration(
  configId: string,
  organizationId: string,
): Promise<void> {
  const { apiRequest } = await import('./api');
  await apiRequest(`/test-configurations/${configId}`, {
    method: 'DELETE',
  }, organizationId);
}

/**
 * Duplicate a test configuration
 */
export async function duplicateTestConfiguration(
  configId: string,
  organizationId: string,
  userId: string,
  newName?: string
): Promise<TestConfiguration> {
  const existing = await getTestConfiguration(configId, organizationId);
  if (!existing) {
    throw new Error('Test configuration not found');
  }

  return createTestConfiguration(organizationId, userId, {
    name: newName || `${existing.name} (Copy)`,
    description: existing.description,
    // Copy flow reference
    flowId: existing.flowId,
    flowName: existing.flowName,
    flowType: existing.flowType,
    flowVersion: existing.flowVersion,
    // Copy execution settings
    locationRequirements: existing.locationRequirements,
    timing: existing.timing,
    esim: existing.esim,
    priority: existing.priority,
    useEmulator: existing.useEmulator,
  });
}

/**
 * Save a finished run as a new configuration (History → Save as
 * configuration). Built from the run's own snapshot, so it repeats what was
 * run — flow, location, eSIM, forced network type / network, flow inputs
 * (APN) — even if the configuration it came from was edited or deleted since.
 * The run itself is not touched. The new configuration runs on demand: a
 * schedule the run came from is not copied, and neither is a one-run approval
 * to move a limited-download eSIM. The name is made unique in the org.
 */
export async function saveRunAsConfiguration(
  organizationId: string,
  userId: string,
  run: TestRun,
): Promise<TestConfiguration> {
  const snap: any = run.configuration || {};
  const existing = await getTestConfigurations(organizationId);
  // A Run Test run that wasn't saved is snapshotted as "Inline Configuration";
  // name its configuration after the flow instead.
  const baseName = snap.id === 'inline' ? run.flowName : snap.name || run.flowName;
  const name = uniqueConfigName(baseName || '', existing.map((c) => c.name || ''));
  const esim = { ...(snap.esim || { mode: 'none' }) };
  delete esim.allowRelocation;

  return createTestConfiguration(organizationId, userId, {
    name,
    description: snap.description || undefined,
    flowId: snap.flowId || run.flowId,
    flowName: snap.flowName || run.flowName,
    flowType: snap.flowType || run.flowType,
    flowVersion: snap.flowVersion ?? run.flowVersion,
    locationRequirements: snap.locationRequirements || [],
    timing: { type: 'now' },
    esim,
    priority: snap.priority,
    useEmulator: snap.useEmulator,
    forcedRat: snap.forcedRat,
    forcedNetworkMccMnc: snap.forcedNetworkMccMnc,
    timeZone: snap.timeZone,
    inputs: snap.inputs && Object.keys(snap.inputs).length > 0 ? snap.inputs : undefined,
  });
}

/**
 * Get most used test configurations
 */
export async function getMostUsedConfigurations(
  organizationId: string,
  limitCount: number = 5
): Promise<TestConfiguration[]> {
  const q = query(
    collection(db, 'test_configurations'),
    where('organizationId', '==', organizationId),
    orderBy('usageCount', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToTestConfiguration(doc.id, doc.data()));
}

/**
 * Get recently used test configurations
 */
export async function getRecentlyUsedConfigurations(
  organizationId: string,
  limitCount: number = 5
): Promise<TestConfiguration[]> {
  const q = query(
    collection(db, 'test_configurations'),
    where('organizationId', '==', organizationId),
    where('lastUsedAt', '!=', null),
    orderBy('lastUsedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToTestConfiguration(doc.id, doc.data()));
}

// ============================================================================
// TEST RUNS
// ============================================================================

/**
 * Get all test runs for an organization
 */
export async function getTestRuns(
  organizationId: string,
  options: {
    status?: ExecutionStatus | ExecutionStatus[];
    flowId?: string;
    limitCount?: number;
  } = {}
): Promise<TestRun[]> {
  let q = createOrgScopedQuery('test_runs', organizationId, [
    orderBy('createdAt', 'desc'),
  ]);

  if (options.status) {
    const statusArray = Array.isArray(options.status) ? options.status : [options.status];
    q = query(q, where('status', 'in', statusArray));
  }

  if (options.flowId) {
    q = query(q, where('flowId', '==', options.flowId));
  }

  if (options.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToTestRun(doc.id, doc.data()));
}

/**
 * Get upcoming (scheduled) test runs
 */
export async function getUpcomingTestRuns(
  organizationId: string,
  limitCount: number = 20
): Promise<TestRun[]> {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'test_runs'),
    where('organizationId', '==', organizationId),
    where('status', '==', 'initializing'),
    where('scheduledFor', '>=', now),
    orderBy('scheduledFor', 'asc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToTestRun(doc.id, doc.data()));
}

/**
 * Get running test runs
 */
export async function getRunningTestRuns(
  organizationId: string,
  limitCount: number = 20
): Promise<TestRun[]> {
  return getTestRuns(organizationId, {
    status: ['running', 'initializing'],
    limitCount,
  });
}

/**
 * Get completed test runs (history)
 */
export async function getTestRunHistory(
  organizationId: string,
  limitCount: number = 50
): Promise<TestRun[]> {
  return getTestRuns(organizationId, {
    status: ['completed', 'failed', 'cancelled'],
    limitCount,
  });
}

/**
 * Get a single test run by ID
 */
export async function getTestRun(
  runId: string,
  organizationId: string
): Promise<TestRun | null> {
  const docRef = doc(db, 'test_runs', runId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const run = docToTestRun(docSnap.id, docSnap.data());

  // Verify tenant isolation
  if (run.organizationId !== organizationId) {
    throw new Error('Unauthorized access to test run');
  }

  return run;
}

/**
 * Create a new test run
 */
export async function createTestRun(
  organizationId: string,
  userId: string,
  data: {
    flowId: string;
    flowName: string;
    flowType: FlowType;
    flowVersion: number;
    testConfigurationId?: string;
    configuration: TestConfiguration;
    scheduledFor?: Date;
  }
): Promise<TestRun> {
  // Sanitize configuration to remove undefined values (Firestore rejects undefined)
  const sanitizedConfig = removeUndefined(data.configuration);

  const runData = {
    organizationId,
    flowId: data.flowId,
    flowName: data.flowName,
    flowType: data.flowType,
    flowVersion: data.flowVersion,
    testConfigurationId: data.testConfigurationId || null,
    configuration: {
      ...sanitizedConfig,
      timing: serializeTiming(data.configuration.timing),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    status: 'initializing' as ExecutionStatus,
    // One-off scheduled runs carry scheduledFor; everything else from this
    // client path is a manual run-now. (Recurring never creates a run here.)
    triggerSource: data.scheduledFor ? 'scheduled' : 'manual',
    createdBy: userId,
    createdAt: serverTimestamp(),
    scheduledFor: data.scheduledFor ? Timestamp.fromDate(data.scheduledFor) : null,
    startedAt: null,
    completedAt: null,
    executionId: null,
    result: null,
  };

  const docRef = await addDoc(collection(db, 'test_runs'), runData);

  // Usage tracking (lastUsedAt + usageCount) is written from the backend
  // when the run reaches the queue dispatcher — see QueueService.addToQueue.
  // The previous client-side updateDoc here failed permission-denied for
  // any non-system config because of an orgIdUnchanged() evaluation fault,
  // which orphaned every Run Now from /tests (see plans/tests-page-audit.md).

  return {
    id: docRef.id,
    organizationId,
    flowId: data.flowId,
    flowName: data.flowName,
    flowType: data.flowType,
    flowVersion: data.flowVersion,
    testConfigurationId: data.testConfigurationId,
    configuration: data.configuration,
    status: 'initializing',
    createdBy: userId,
    createdAt: new Date(),
    scheduledFor: data.scheduledFor,
  };
}

/**
 * Cancel a test run
 */
export async function cancelTestRun(
  runId: string,
  organizationId: string,
  reason?: string
): Promise<void> {
  // Server-side cancel (#222) — the backend cascades the cancel to the
  // execution, queue item, and modem allocation so the flow actually stops
  // on the Pi. A direct Firestore doc-flip here only changed the badge while
  // the test kept running.
  await apiRequest(
    `/test-runs/${runId}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Cancelled by user' }),
    },
    organizationId
  );
}

/**
 * Delete a test run
 */
export async function deleteTestRun(
  runId: string,
  organizationId: string
): Promise<void> {
  const existing = await getTestRun(runId, organizationId);
  if (!existing) {
    throw new Error('Test run not found');
  }

  if (existing.status === 'running') {
    throw new Error('Cannot delete a running test. Cancel it first.');
  }

  const docRef = doc(db, 'test_runs', runId);
  await deleteDoc(docRef);
}

/**
 * Get test run statistics
 */
export async function getTestRunStats(organizationId: string): Promise<{
  total: number;
  running: number;
  scheduled: number;
  completed24h: number;
  failed24h: number;
}> {
  const runs = await getTestRuns(organizationId, { limitCount: 500 });

  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const stats = {
    total: runs.length,
    running: 0,
    scheduled: 0,
    completed24h: 0,
    failed24h: 0,
  };

  for (const run of runs) {
    if (run.status === 'running' || run.status === 'initializing') {
      if (run.scheduledFor && run.scheduledFor > now) {
        stats.scheduled++;
      } else {
        stats.running++;
      }
    } else if (run.completedAt && run.completedAt >= oneDayAgo) {
      if (run.status === 'completed') {
        stats.completed24h++;
      } else if (run.status === 'failed') {
        stats.failed24h++;
      }
    }
  }

  return stats;
}

/**
 * Subscribe to test runs in real-time
 */
export function subscribeToTestRuns(
  organizationId: string,
  callback: (runs: TestRun[]) => void,
  options: {
    status?: ExecutionStatus | ExecutionStatus[];
    limitCount?: number;
  } = {}
): Unsubscribe {
  let q = query(
    collection(db, 'test_runs'),
    where('organizationId', '==', organizationId),
    orderBy('createdAt', 'desc')
  );

  if (options.status) {
    const statusArray = Array.isArray(options.status) ? options.status : [options.status];
    q = query(q, where('status', 'in', statusArray));
  }

  if (options.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  return onSnapshot(q, (snapshot) => {
    const runs = snapshot.docs.map((doc) => docToTestRun(doc.id, doc.data()));
    callback(runs);
  });
}

/**
 * Subscribe to upcoming (scheduled) test runs in real-time.
 * Mirrors getUpcomingTestRuns. The `scheduledFor >= now` bound is fixed at
 * subscribe time, but runs that come due leave the list anyway because the
 * scheduler flips their status away from 'initializing'.
 */
export function subscribeToUpcomingTestRuns(
  organizationId: string,
  callback: (runs: TestRun[]) => void,
  limitCount: number = 20
): Unsubscribe {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'test_runs'),
    where('organizationId', '==', organizationId),
    where('status', '==', 'initializing'),
    where('scheduledFor', '>=', now),
    orderBy('scheduledFor', 'asc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((doc) => docToTestRun(doc.id, doc.data())));
  });
}

/**
 * Subscribe to a single test run in real-time
 * Used for the Test Run Details modal to update status when run completes
 */
export function subscribeToTestRun(
  runId: string,
  callback: (run: TestRun | null) => void
): Unsubscribe {
  const docRef = doc(db, 'test_runs', runId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(docToTestRun(snapshot.id, snapshot.data()));
    } else {
      callback(null);
    }
  });
}

// ============================================================================
// BATCH EXECUTIONS
// ============================================================================

/**
 * Get all batch executions for an organization
 */
export async function getBatchExecutions(
  organizationId: string,
  options: {
    status?: BatchExecution['status'];
    limitCount?: number;
  } = {}
): Promise<BatchExecution[]> {
  let q = createOrgScopedQuery('batch_executions', organizationId, [
    orderBy('createdAt', 'desc'),
  ]);

  if (options.status) {
    q = query(q, where('status', '==', options.status));
  }

  if (options.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToBatchExecution(doc.id, doc.data()));
}

/**
 * Get a single batch execution by ID
 */
export async function getBatchExecution(
  batchId: string,
  organizationId: string
): Promise<BatchExecution | null> {
  const docRef = doc(db, 'batch_executions', batchId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const batch = docToBatchExecution(docSnap.id, docSnap.data());

  // Verify tenant isolation
  if (batch.organizationId !== organizationId) {
    throw new Error('Unauthorized access to batch execution');
  }

  return batch;
}

/**
 * Cancel a batch execution
 */
export async function cancelBatchExecution(
  batchId: string,
  organizationId: string,
  reason?: string
): Promise<void> {
  const existing = await getBatchExecution(batchId, organizationId);
  if (!existing) {
    throw new Error('Batch execution not found');
  }

  if (['completed', 'failed', 'partial'].includes(existing.status)) {
    throw new Error(`Cannot cancel batch with status: ${existing.status}`);
  }

  // Cancel all pending test runs
  for (const runId of existing.testRunIds) {
    try {
      const run = await getTestRun(runId, organizationId);
      if (run && !['completed', 'failed', 'cancelled'].includes(run.status)) {
        await cancelTestRun(runId, organizationId, reason);
      }
    } catch {
      console.warn(`Failed to cancel run ${runId}`);
    }
  }

  const docRef = doc(db, 'batch_executions', batchId);
  await updateDoc(docRef, {
    status: 'failed',
    completedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to batch executions in real-time
 */
export function subscribeToBatchExecutions(
  organizationId: string,
  callback: (batches: BatchExecution[]) => void,
  options: {
    status?: BatchExecution['status'];
    limitCount?: number;
  } = {}
): Unsubscribe {
  let q = query(
    collection(db, 'batch_executions'),
    where('organizationId', '==', organizationId),
    orderBy('createdAt', 'desc')
  );

  if (options.status) {
    q = query(q, where('status', '==', options.status));
  }

  if (options.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  return onSnapshot(q, (snapshot) => {
    const batches = snapshot.docs.map((doc) => docToBatchExecution(doc.id, doc.data()));
    callback(batches);
  });
}
